import { AIGenerateButton } from './AIGenerateButton'
import Styles from './MobileImageButtons.module.scss'
import { ChangeEvent, forwardRef } from 'react'
import { useObjectRef } from '@react-aria/utils'
import { useButton } from '@react-aria/button'
import { Background } from 'blaze-ui/icons/32'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQueryClient } from 'react-query'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { Icon } from 'blaze-ui/icons/Types'
import { Camera } from 'blaze-ui/icons/20/Camera'
import cx from 'classnames'
import { ACCEPTED_IMAGE_MIME_TYPES_NO_SVG } from 'screens/Finder/components/views/GalleryView/constants'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { AlmanacFile } from 'types'

interface Props {
  capture?: 'user' | 'environment'
  icon: Icon
  label: string
  onConfirm?: (images?: AlmanacFile[]) => void
  onChangeInput?: (e: ChangeEvent<HTMLInputElement>) => void
  buttonClassName?: string
}

export const MobileImageButton = forwardRef<HTMLInputElement, Pick<ButtonProps, 'variant' | 'onPress'> & Props>(
  (
    {
      capture,
      icon: Icon,
      label,
      onPress,
      onChangeInput,
      variant = 'tertiary',
      onConfirm,
      buttonClassName,
    }: Pick<ButtonProps, 'variant' | 'onPress'> & Props,
    fRef,
  ) => {
    const workspaceId = useCurrentWorkspaceId()
    const queryClient = useQueryClient()
    const uploadsProjectQuery = useUploadsProject(workspaceId)
    const uploadsProjectId = uploadsProjectQuery.data?.project?.id
    const ref = useObjectRef(fRef)

    const { buttonProps, isPressed } = useButton(
      {
        onPress: (e) => {
          if (onPress) {
            onPress(e)
          } else {
            fileInputRef.current?.click()
          }
        },
        elementType: 'div',
      },
      ref,
    )
    const { fileInputRef, uploadImagesFromEvent } = useImportMediaFiles(false, (images) => {
      if (uploadsProjectId) {
        queryClient.invalidateQueries(['workspace', workspaceId, 'files', uploadsProjectId])
      }

      onConfirm?.(images)
    })

    return (
      <div
        {...buttonProps}
        className={cx(Styles.mobileImageButton, Styles[variant], { [Styles.pressed]: isPressed }, buttonClassName)}
        ref={ref}
      >
        <Icon />
        <input
          className={Styles.fileInput}
          aria-label={label}
          ref={fileInputRef}
          type="file"
          multiple
          onChange={(event) => {
            if (onChangeInput) {
              onChangeInput(event)
              return
            }
            uploadImagesFromEvent(event, uploadsProjectId)
          }}
          accept={ACCEPTED_IMAGE_MIME_TYPES_NO_SVG}
          capture={capture}
        />
        <div className={Styles.label}>{label}</div>
      </div>
    )
  },
)

export const MobileImageButtons = () => (
  <div className={Styles.mobileButtonsContainer}>
    <div className={Styles.mobileButtonsHorizontalContainer}>
      <MobileImageButton label="Photos & Files" icon={() => <Background size={30} />} />
      <MobileImageButton label="Camera" icon={() => <Camera size={30} />} capture="environment" />
    </div>
    <AIGenerateButton />
  </div>
)
